import React, { useEffect, useMemo, useState } from 'react';
import { useSaveStatusStore } from '../store/saveStatusStore';
import { useIDEStore } from '../store/ideStore';

/**
 * AutoSaveBadge — chrome-level indicator of auto-save status.
 *
 * Mounted in the Layout header next to WebSocketStatus, so students can see
 * at a glance whether their work has been persisted. The in-panel
 * SaveIndicator at the bottom of IDE.tsx remains for per-file detail; this
 * badge is the always-visible summary.
 *
 * Aggregate rule (highest priority wins):
 *   error    — any file's most recent save errored
 *   saving   — any file is currently being saved
 *   dirty    — at least one open file has unsaved changes
 *   saved    — last successful save < 30s ago
 *   idle     — otherwise (nothing to do, all caught up)
 *
 * "dirty" is derived from the ideStore (isDirty per file) rather than from
 * saveStatuses, because saveStatuses only carries entries we've attempted
 * to save — a fresh keystroke marks the file dirty in ideStore before any
 * save status exists yet.
 */

type Aggregate = 'error' | 'saving' | 'dirty' | 'saved' | 'idle';

const RECENT_SAVE_WINDOW_MS = 30_000;
const TICK_MS = 15_000;

interface Look {
  dot: string;
  bg: string;
  label: string;
  pulse: boolean;
}

const LOOKS: Record<Aggregate, Look> = {
  error:  { dot: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', label: 'Save failed', pulse: false },
  saving: { dot: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', label: 'Saving…',    pulse: true  },
  dirty:  { dot: '#f59e0b', bg: 'rgba(245, 158, 11, 0.10)', label: 'Unsaved',    pulse: false },
  saved:  { dot: '#22c55e', bg: 'rgba(34, 197, 94, 0.10)',  label: 'Saved',      pulse: false },
  idle:   { dot: '#6b7280', bg: 'rgba(107, 114, 128, 0.10)',label: 'Up to date', pulse: false },
};

function formatRelative(date: Date, now: number): string {
  const seconds = Math.floor((now - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

const AutoSaveBadge: React.FC = () => {
  const statuses = useSaveStatusStore((s) => s.statuses);
  const active = useSaveStatusStore((s) => s.active);
  const retry = useSaveStatusStore((s) => s.retry);

  // Subscribe to files map to derive dirty count. Zustand only re-renders when
  // the selected reference changes; we collapse to a small primitive (count)
  // so unrelated file-content edits don't re-render the header on every key.
  const dirtyCount = useIDEStore((s) => {
    let n = 0;
    s.files.forEach((f) => { if (f.isDirty) n += 1; });
    return n;
  });

  // Tick the clock so "Saved Xs ago" in the tooltip stays roughly fresh.
  // We use a coarse 15s tick (not 1s) to avoid header re-renders every second,
  // and we pause entirely when the tab is hidden to keep CPU low for inactive
  // student tabs.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer != null) return;
      setNow(Date.now()); // refresh immediately on (re-)start
      timer = setInterval(() => setNow(Date.now()), TICK_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    if (!document.hidden) start();
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const { aggregate, errored, lastSaved } = useMemo(() => {
    const errored = statuses.find((s) => s.status === 'error');
    const saving  = statuses.some((s) => s.status === 'saving');
    const savedEntries = statuses
      .filter((s) => s.status === 'saved' && s.lastSaved)
      .sort((a, b) => (b.lastSaved!.getTime() - a.lastSaved!.getTime()));
    const lastSaved = savedEntries[0]?.lastSaved;

    let agg: Aggregate;
    if (errored) agg = 'error';
    else if (saving) agg = 'saving';
    else if (dirtyCount > 0) agg = 'dirty';
    else if (lastSaved && now - lastSaved.getTime() < RECENT_SAVE_WINDOW_MS) agg = 'saved';
    else agg = 'idle';

    return { aggregate: agg, errored, lastSaved };
  }, [statuses, dirtyCount, now]);

  if (!active) return null;

  const look = LOOKS[aggregate];

  // Tooltip text — richer than the visible label, includes timing / error.
  const title = (() => {
    switch (aggregate) {
      case 'error':
        return errored?.error
          ? `Auto-save failed for ${errored.path}: ${errored.error}. Click Retry.`
          : 'Auto-save failed. Click Retry.';
      case 'saving':
        return 'Saving your changes…';
      case 'dirty':
        return `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'} — auto-save will run shortly`;
      case 'saved':
        return lastSaved
          ? `All changes saved ${formatRelative(lastSaved, now)}`
          : 'All changes saved';
      case 'idle':
      default:
        return 'No unsaved changes';
    }
  })();

  const pulseAnimation = look.pulse && typeof document !== 'undefined' && !document.hidden
    ? 'autoSavePulse 1.4s ease-in-out infinite'
    : undefined;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`Auto-save: ${look.label}`}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: look.bg,
        color: '#e0e0e0',
        fontSize: 12,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: look.dot,
          animation: pulseAnimation,
        }}
      />
      <span>{look.label}</span>
      {aggregate === 'error' && errored && retry && (
        <button
          type="button"
          onClick={() => retry(errored.path)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fca5a5',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: 12,
            padding: 0,
            marginLeft: 4,
          }}
        >
          Retry
        </button>
      )}
      <style>{`
        @keyframes autoSavePulse {
          0%   { opacity: 1;   transform: scale(1); }
          50%  { opacity: 0.5; transform: scale(1.25); }
          100% { opacity: 1;   transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AutoSaveBadge;
